import * as React from 'react';
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {
  BANNER_CANVAS,
  BANNER_GEOMETRY,
  bannerHeroHeight,
  type BannerBrand,
  type BannerListing,
  type BannerRatio,
  ListingBanner,
  bannerHeight,
} from '@proptr/creative-templates';

// The listing creative, in motion.
//
// Deliberately NOT a second design. The still template is mounted as-is
// and animated around: the photographs cross-fade behind it with a slow
// push, and the furniture springs up over the top. Every colour, inset and
// type size still comes from BANNER_GEOMETRY, so a change to the still
// lands in the video for free and the two cannot drift.
//
// What this file owns is timing. What the template owns is design.

// A type alias, not an interface: Remotion constrains composition props to
// `Record<string, unknown>`, and only aliases get an implicit index
// signature. An interface here fails to assign for no visible reason.
export type ListingVideoProps = {
  brand: BannerBrand;
  listing: BannerListing;
  ratio: BannerRatio;
  /**
   * Optional soundtrack. Remotion trims it to the composition, so a track
   * longer than the clip simply stops — no need to cut the file.
   *
   * The URL must be CORS-readable: the Player fetches it from the browser
   * and the renderer from headless Chrome, and a bucket that blocks
   * cross-origin reads fails in both.
   */
  music?: { url: string; volume?: number } | null;
};

/** Most photos a clip will cycle through. */
export const MAX_PHOTOS = 4;
/** How long each photo holds, including its cross-fade. */
export const SECONDS_PER_PHOTO = 2.2;
/** Overlap between one photo and the next. */
const CROSSFADE_SECONDS = 0.7;
/** Tail over which the music fades out, so the clip doesn't cut dead. */
const MUSIC_FADE_SECONDS = 1;

/**
 * The photos a clip cycles through: the hero first, then the gallery.
 *
 * SQUARE ONLY. The 9:16 already shows the gallery as a rail along its
 * foot, so cycling the same shots through its hero would play them twice
 * at once — the rail tile and the hero would be the same photograph. The
 * square has no rail, which is exactly why it has the room to do this.
 *
 * De-duplicated because a listing whose gallery repeats the hero would
 * otherwise cross-fade an image into itself, which reads as the video
 * stalling rather than as a transition.
 */
export function photosFor(
  listing: BannerListing,
  ratio: BannerRatio,
): string[] {
  const hero = listing.imageUrl ? [listing.imageUrl] : [];
  if (ratio === '9:16') return hero;
  const all = [listing.imageUrl, ...(listing.gallery ?? [])].filter(
    (u): u is string => typeof u === 'string' && u.length > 0,
  );
  return [...new Set(all)].slice(0, MAX_PHOTOS);
}

/** Clip length for a photo count. One photo still gets a full beat. */
export function videoDurationFrames(photoCount: number, fps: number): number {
  return Math.round(Math.max(1, photoCount) * SECONDS_PER_PHOTO * fps);
}

export const ListingVideo: React.FC<ListingVideoProps> = ({
  brand,
  listing,
  ratio,
  music,
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const photos = photosFor(listing, ratio);
  const segment = durationInFrames / Math.max(1, photos.length);
  const fade = CROSSFADE_SECONDS * fps;

  // The marks fade up together once the first photo has established
  // itself. Spring rather than linear so it settles rather than stopping.
  const furniture = spring({
    frame: frame - Math.round(fps * 0.4),
    fps,
    config: { damping: 200 },
    durationInFrames: Math.round(fps * 0.8),
  });

  // Everything the still needs, minus the hero — the photos are animated
  // separately underneath so the type doesn't move with them. The rail
  // stays: its tiles are part of the design, not decoration.
  const listingWithoutHero: BannerListing = { ...listing, imageUrl: null };

  // The template paints an opaque `ink` frame behind its photo. Stacked
  // over the animated hero that frame hides it completely — which is
  // exactly what a first render looks like when it comes out black.
  const overlayBrand: BannerBrand = { ...brand, ink: 'transparent' };

  // With the ground transparent, the moving photos would otherwise show
  // through the gaps BETWEEN the rail tiles. Clipping them to the hero —
  // the same height the template gives its own photo — puts the ink back
  // behind the rail and reproduces the still exactly.
  const geo = BANNER_GEOMETRY.social[ratio];
  const gallery = listing.gallery ?? [];
  const heroH = bannerHeroHeight(ratio, gallery.length, 'social');
  const hasRail = !!geo.rail && gallery.length >= geo.rail.tiles;
  const railRadius = geo.rail?.radius ?? 0;

  const musicFade = MUSIC_FADE_SECONDS * fps;

  return (
    <AbsoluteFill style={{ backgroundColor: brand.ink }}>
      {music?.url && (
        <Audio
          src={music.url}
          // Ramp the tail to silence rather than letting the clip cut mid
          // bar, which is the single thing that makes a generated video
          // sound generated.
          volume={(f) =>
            interpolate(
              f,
              [durationInFrames - musicFade, durationInFrames],
              [music.volume ?? 1, 0],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )
          }
        />
      )}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: heroH,
          overflow: 'hidden',
          // Bleeds off the top; only the bottom corners round, meeting the
          // rail's radius the way the still's hero does.
          borderRadius: hasRail ? `0 0 ${railRadius}px ${railRadius}px` : 0,
        }}
      >
        {photos.map((src, i) => {
          const start = i * segment;
          const isFirst = i === 0;
          const isLast = i === photos.length - 1;

          // Cross-fade in, hold, cross-fade out. The first photo is already
          // up at frame 0 and the last one never leaves, so neither fades
          // against the ink — that reads as a flicker at the clip's edges
          // rather than as a transition.
          const opacity = interpolate(
            frame,
            [start - fade, start, start + segment - fade, start + segment],
            [isFirst ? 1 : 0, 1, 1, isLast ? 1 : 0],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );

          // Every photo pushes in. Alternating the direction was an
          // attempt to keep consecutive beats distinct, but a pull-out
          // reads as the camera retreating from the property — the wrong
          // gesture for a listing. The cross-fade already separates them.
          //
          // The push starts when the photo first becomes VISIBLE, not at
          // its nominal start — it fades in `fade` frames early, and
          // beginning the scale at `start` left it frozen on screen for
          // the whole cross-fade before anything moved.
          const scale = interpolate(
            frame,
            [start - fade, start + segment],
            [1, 1.08],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );

          return (
            <AbsoluteFill key={`${src}-${i}`} style={{ opacity }}>
              <AbsoluteFill
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: 'center center',
                }}
              >
                <Img
                  src={src}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </AbsoluteFill>
            </AbsoluteFill>
          );
        })}
      </div>

      <AbsoluteFill style={{ opacity: furniture }}>
        <ListingBanner
          width={BANNER_CANVAS}
          ratio={ratio}
          variant="social"
          brand={overlayBrand}
          listing={listingWithoutHero}
        />
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

/** Canvas size for a ratio, shared with the still template. */
export function videoSize(ratio: BannerRatio) {
  return { width: BANNER_CANVAS, height: bannerHeight(ratio, 'social') };
}
